/**
 * Import Validation Helper
 *
 * Centralized validation logic for import pages.
 * Works with validation configs defined in importValidationConfigs.ts
 */

import { ImportValidationConfig } from '../config/importValidationConfigs';
import { ValidationHelper } from './ValidationHelper';
import { SharedMsgHelper } from './SharedMsgHelper';
import * as _ from 'lodash';

export const ImportValidationHelper = {
    /**
     * Validates a single row against the validation config
     * Returns the row with error messages in place of invalid values
     *
     * @param row - The data row to validate
     * @param config - The validation configuration
     * @param headerValues - Optional array of header values to filter the row
     * @returns Validated row with error messages for invalid fields
     */
    validateRow(row: any, config: ImportValidationConfig, headerValues?: string[]): any {
        // Trim all values and filter by headers if provided
        let result = headerValues
            ? _.mapValues(_.pick(row, headerValues), (value) => _.trim(value))
            : _.mapValues(row, (value) => (typeof value === 'string' ? _.trim(value) : value));

        // Validate each field according to config
        Object.keys(config).forEach((fieldName) => {
            const fieldConfig = config[fieldName];
            const value = result[fieldName];

            // Check required
            if (fieldConfig.required && !value) {
                result[fieldName] = SharedMsgHelper.getMissingRequiredErrMsg;
                return;
            }

            // Skip further validation if empty and not required
            if (!value) return;

            // Type validations
            if (fieldConfig.type === 'number' && !ValidationHelper.isNumber(value)) {
                result[fieldName] = SharedMsgHelper.getInvalidAmountErrMsg;
            } else if (fieldConfig.type === 'integer' && !ValidationHelper.isInteger(value)) {
                result[fieldName] = SharedMsgHelper.getInvalidIntegerErrMsg;
            } else if (fieldConfig.type === 'date' && !ValidationHelper.isStandardDateFormat(value)) {
                result[fieldName] = SharedMsgHelper.getInvalidDateErrMsg;
            } else if (fieldConfig.type === 'enum' && fieldConfig.enumValues) {
                if (!fieldConfig.enumValues.includes(value)) {
                    result[fieldName] = `Invalid ${fieldName}`;
                }
            }

            // Custom validator
            if (fieldConfig.customValidator) {
                const error = fieldConfig.customValidator(value);
                if (error) {
                    result[fieldName] = error;
                }
            }
        });

        return result;
    },

    /**
     * Checks if a row has any validation errors
     *
     * @param row - The data row to check
     * @param config - The validation configuration
     * @returns true if row has validation errors, false otherwise
     */
    hasValidationErrors(row: any, config: ImportValidationConfig): boolean {
        const errorMessages = [
            SharedMsgHelper.getMissingRequiredErrMsg,
            SharedMsgHelper.getInvalidAmountErrMsg,
            SharedMsgHelper.getInvalidIntegerErrMsg,
            SharedMsgHelper.getInvalidDateErrMsg
        ];

        return Object.keys(config).some((fieldName) => {
            const value = row[fieldName];
            // Check if value is one of the standard error messages
            if (errorMessages.includes(value)) {
                return true;
            }
            // Check if value starts with "Invalid " (for enum validation errors)
            if (typeof value === 'string' && value.startsWith('Invalid ')) {
                return true;
            }
            return false;
        });
    },

    /**
     * Validates a list of rows against the validation config
     *
     * @param rows - Array of rows to validate
     * @param config - The validation configuration
     * @param headerValues - Optional array of header values to filter each row
     * @returns Array of validated rows with error messages
     */
    validateRows(rows: any[], config: ImportValidationConfig, headerValues?: string[]): any[] {
        return _.map(rows, (row) => this.validateRow(row, config, headerValues));
    },

    /**
     * Checks if all rows are valid
     *
     * @param rows - Array of rows to check
     * @param config - The validation configuration
     * @returns true if all rows are valid, false otherwise
     */
    areAllRowsValid(rows: any[], config: ImportValidationConfig): boolean {
        return _.every(rows, (row) => !this.hasValidationErrors(row, config));
    }
};
